import { useCallback } from 'react';

import { useDirectoryUpdateRecord } from 'api/requests/directory/record/useDirectoryUpdateRecord';
import {
  DirectoryRecordData,
  DirectorySchemaData,
} from 'api/types/data/directory/directory';
import { PermissionType } from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { useDirectoryLockAndUnlockRecord } from 'modules/directory/common/hooks';

import { getUpdateSubmitData } from '../helpers';

interface Props {
  currentRecord?: DirectoryRecordData;
  currentSchema?: DirectorySchemaData;
}

export const useUpdateRecord = ({ currentSchema, currentRecord }: Props) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { checkPermissions } = usePermissions();

  const {
    updateRecord,
    loading: submitLoading,
    error,
  } = useDirectoryUpdateRecord();

  const { lockDirectoryIfPossible, unlockDirectory } =
    useDirectoryLockAndUnlockRecord({
      id: currentRecord?.id,
      versionNumber: currentRecord?.versionNumber,
    });

  const isCanEdit =
    !currentRecord?.isArchived &&
    checkPermissions([PermissionType.Directory_UpdateOne]);

  const onSubmit: FormSubmit<{}> = async (values, helpers) => {
    if (currentSchema && currentRecord) {
      const data = getUpdateSubmitData(currentSchema, values);
      await updateRecord(currentRecord.id, currentRecord.versionNumber!, {
        fields: data.fields,
      });
      helpers.setReadOnly(true);
      notify.info(t('common.successSave'));
    }
  };

  // Enable edit mode
  const onEnableEdit = useCallback(
    async (onCallback: () => void) => {
      const isPossibleEdit = await lockDirectoryIfPossible?.();

      if (isPossibleEdit) {
        onCallback();
      }
    },
    [lockDirectoryIfPossible]
  );

  // Disable edit mode
  const onDisableEdit = useCallback(
    async (onCallback: () => void) => {
      unlockDirectory?.();
      onCallback();
    },
    [unlockDirectory]
  );

  useServerErrorNotify(error);

  return {
    isCanEdit,
    submitLoading,
    onEnableEdit,
    onDisableEdit,
    onSubmit,
  };
};
